// CreateSubmitModal - Custom UI for Create Instance Operations
// Based on OnSubmitModal from EditInstance but adapted for creation

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Modal,
  ModalFooter,
  Button,
  Spinner,
  Accordion,
  Row,
  Col,
  Badge,
} from '@folio/stripes/components';
import { FaInfoCircle } from 'react-icons/fa';
import { ErrorDetailsModal } from '../../../../shared/ErrorHandling/components';
import { UniversalProgressTracker } from '../../../../shared/ProgressTracking/components';

const CreateSubmitModal = ({
  open,
  onClose,
  processCounter,
  instanceProgressMsg,
  holdingsProgressMsg,
  itemsProgressMsg,
  errorMessages,
  totalProcesses,
  startUpdate,
  setStartUpdate,
  processesSammary,
  isProcessing,
  currentBatch,
  showErrors,
  setShowErrors,
  formValues,
  handleCloseModal,
}) => {
  const intl = useIntl();
  const [expandedSections, setExpandedSections] = useState({
    instance: true,
    holdings: true,
    items: true,
    errors: false,
  });
  const [showErrorModal, setShowErrorModal] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };






  const getModalTitle = () => {
    if (!startUpdate) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.createProgress.confirmTitle"
          defaultMessage="Confirm Instance Creation"
        />
      );
    }

    if (isProcessing) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.createProgress.processingTitle"
          defaultMessage="Creating Instance..."
        />
      );
    }

    return (
      <FormattedMessage
        id="ui-plugin-inventory-templates.createProgress.completedTitle"
        defaultMessage="Instance Creation Completed"
      />
    );
  };

  const renderConfirmationStep = () => {
    return (
      <div>
        <div style={{ marginBottom: '20px' }}>
          <h3>
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.confirmMessage"
              defaultMessage="You are about to create a new instance with the following data:"
            />
          </h3>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.instanceTitle"
              defaultMessage="Instance Title:"
            />
          </h4>
          <div
            style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              fontWeight: 'bold',
            }}
          >
            {formValues.instanceData?.title || <FormattedMessage id="ui-plugin-inventory-templates.instanceTitle.untitled" />}
          </div>
        </div>

        <UniversalProgressTracker
          mode="create"
          contextData={formValues}
          showDetailedStats={false}
          showBatchInfo={false}
          showAccordion={false}
        />

        <div
          style={{
            marginTop: '20px',
            padding: '12px',
            backgroundColor: '#d1ecf1',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <FaInfoCircle size={18} style={{ color: '#17a2b8', flexShrink: 0 }} />
          <span>
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.confirmNote"
              defaultMessage="This will create a new instance record along with all holdings and items. Click 'Start Creation' to proceed."
            />
          </span>
        </div>
      </div>
    );
  };

  const renderProcessingStep = () => {
    return (
      <UniversalProgressTracker
        mode="create"
        current={processCounter}
        total={totalProcesses}
        currentBatch={currentBatch}
        totalBatches={0}
        summary={processesSammary}
        instanceMessages={instanceProgressMsg}
        holdingsMessages={holdingsProgressMsg}
        itemsMessages={itemsProgressMsg}
        showDetailedStats={true}
        showBatchInfo={false}
        showAccordion={true}
        progressColor="#28a745"
        progressHeight="8px"
        showInlineText={false}
      />
    );
  };

  const renderFooter = () => {
    if (!startUpdate) {
      return (
        <ModalFooter>
          <Button buttonStyle="primary" onClick={() => setStartUpdate(true)}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.startCreation"
              defaultMessage="Start Creation"
            />
          </Button>
          <Button onClick={onClose}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.cancel"
              defaultMessage="Cancel"
            />
          </Button>
        </ModalFooter>
      );
    }

    // Check if processing is completed
    const isCompleted = processCounter === totalProcesses && startUpdate && totalProcesses > 0 && !isProcessing;

    return (
      <ModalFooter>
        {/* Show loading spinner and message when processing */}
        {isProcessing && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginRight: 'auto',
            color: '#007bff'
          }}
          >
            <Spinner size="small" style={{ marginRight: '8px' }} />
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.processing"
              defaultMessage="Creating instance and related records..."
            />
          </div>
        )}

        {errorMessages.length > 0 && !isProcessing && (
          <Button
            buttonStyle="default"
            onClick={() => setShowErrorModal(true)}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.errors.details"
              defaultMessage="Error Details"
            />
          </Button>
        )}

        {/* Show Finish button when completed (with or without errors) */}
        {isCompleted ? (
          <Button
            buttonStyle="primary"
            onClick={handleCloseModal}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.createProgress.finish"
              defaultMessage="Finish"
            />
          </Button>
        ) : (
          !isProcessing && (
            <Button onClick={onClose}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.createProgress.close"
                defaultMessage="Close"
              />
            </Button>
          )
        )}
      </ModalFooter>
    );
  };



  return (
    <>
      <ErrorDetailsModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        errors={errorMessages}
        contextData={formValues}
        titleKey="ui-plugin-inventory-templates.createProgress.errors.modalTitle"
        mode="create"
      />
      <Modal
        open={open}
        onClose={onClose}
        label={getModalTitle()}
        dismissible={!isProcessing}
        size="large"
        style={{ maxWidth: '900px' }}
        footer={renderFooter()}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '70vh', minHeight: '400px' }}>


          {/* Scrollable content area */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            overscrollBehavior: 'contain'
          }}
          >
            {!startUpdate ? renderConfirmationStep() : renderProcessingStep()}
          </div>
        </div>
      </Modal>
    </>
  );
};

CreateSubmitModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  processCounter: PropTypes.number,
  instanceProgressMsg: PropTypes.array,
  holdingsProgressMsg: PropTypes.array,
  itemsProgressMsg: PropTypes.array,
  errorMessages: PropTypes.array,
  totalProcesses: PropTypes.number,
  startUpdate: PropTypes.bool,
  setStartUpdate: PropTypes.func.isRequired,
  processesSammary: PropTypes.array,
  isProcessing: PropTypes.bool,
  currentBatch: PropTypes.number,
  showErrors: PropTypes.bool,
  setShowErrors: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  handleCloseModal: PropTypes.func,
};

CreateSubmitModal.defaultProps = {
  processCounter: 0,
  instanceProgressMsg: [],
  holdingsProgressMsg: [],
  itemsProgressMsg: [],
  errorMessages: [],
  totalProcesses: 0,
  startUpdate: false,
  processesSammary: [],
  isProcessing: false,
  currentBatch: 0,
  showErrors: false,
  formValues: {},
  handleCloseModal: () => {},
};

export default CreateSubmitModal;
